import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Icon from "@mui/material/Icon";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { get } from "lib/api";
import { firstListMediaUrl } from "lib/media";
import { useAuth } from "context/AuthContext";
import { currencyForCompany } from "lib/companyProfile";
import useStorefrontCopy from "hooks/useStorefrontCopy";
import VbsShell from "../components/VbsShell";
import { VbsGridTile } from "../components/VbsProductCard";
import { formatMoney, normalizeTransmissions, resolvePrice, vbs } from "../lib/theme";

function normalizeProduct(item) {
  const attrs = item?.attributes || item || {};
  return {
    documentId: item?.documentId || attrs?.documentId,
    sku: attrs.sku,
    name: attrs.name,
    unitPrice: attrs.unitPrice,
    priceLevels: attrs.priceLevels,
    images: attrs.images,
    transmissions: normalizeTransmissions(attrs.transmissions),
    imageUrl: firstListMediaUrl(attrs.images),
  };
}

function FilterPill({ active, label, onClick }) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        flex: "0 0 auto",
        px: 1.35,
        py: 0.65,
        borderRadius: "999px",
        border: `1px solid ${active ? vbs.accent : vbs.hairline}`,
        bgcolor: active ? vbs.accentSoft : vbs.surface,
        color: active ? vbs.accent : vbs.ink,
        fontSize: 13,
        fontWeight: 700,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </ButtonBase>
  );
}

FilterPill.propTypes = {
  active: PropTypes.bool.isRequired,
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
};

export default function CatalogPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const q = String(searchParams.get("q") || "");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedTransmission, setSelectedTransmission] = useState("");
  const { token, storefrontTenant, activeCompany } = useAuth();
  const { t } = useStorefrontCopy();
  const companyKey = String(storefrontTenant || activeCompany || "vbsus").toLowerCase();

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(q), 300);
    return () => clearTimeout(timer);
  }, [q]);

  const { data, isLoading } = useQuery({
    queryKey: ["vbsus-mobile-catalog", debouncedSearch, companyKey, token ? "auth" : "public"],
    queryFn: () =>
      get("/api/storefront/catalog", {
        q: debouncedSearch || undefined,
        sort: "name:asc",
        pagination: { pageSize: 100 },
      }),
    staleTime: 30000,
  });

  const currency = data?.meta?.company?.baseCurrency || currencyForCompany(companyKey);
  const products = useMemo(() => (data?.data || []).map(normalizeProduct), [data]);

  const transmissionOptions = useMemo(() => {
    const set = new Set();
    products.forEach((product) => (product.transmissions || []).forEach((value) => set.add(value)));
    return Array.from(set).sort().slice(0, 8);
  }, [products]);

  const visibleProducts = useMemo(() => {
    if (!selectedTransmission) return products;
    return products.filter((product) =>
      (product.transmissions || []).some((value) => value.toLowerCase() === selectedTransmission.toLowerCase()),
    );
  }, [products, selectedTransmission]);

  const setSearch = (value) => {
    const next = String(value || "").trim();
    if (next) setSearchParams({ q: next });
    else setSearchParams({});
  };

  return (
    <VbsShell title={t("common.catalog", "Products")} onBack={() => navigate("/m")}>
      <TextField
        fullWidth
        value={q}
        onChange={(event) => setSearch(event.target.value)}
        placeholder={t("common.searchPlaceholder", "Search valve bodies...")}
        variant="standard"
        InputProps={{
          disableUnderline: false,
          startAdornment: (
            <InputAdornment position="start">
              <Icon sx={{ color: vbs.subtle, fontSize: 22 }}>search</Icon>
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 1.5,
          "& .MuiInput-underline:before": { borderBottomColor: vbs.hairline },
          "& .MuiInput-underline:after": { borderBottomColor: vbs.accent },
          "& .MuiInputBase-input": { fontSize: 16, py: 0.75 },
        }}
      />

      {transmissionOptions.length ? (
        <Stack
          direction="row"
          spacing={0.65}
          sx={{
            overflowX: "auto",
            pb: 1.75,
            mx: -2,
            px: 2,
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          <FilterPill
            active={!selectedTransmission}
            label={t("common.allTransmissions", "All")}
            onClick={() => setSelectedTransmission("")}
          />
          {transmissionOptions.map((option) => (
            <FilterPill
              key={option}
              active={selectedTransmission === option}
              label={option}
              onClick={() => setSelectedTransmission(option)}
            />
          ))}
        </Stack>
      ) : null}

      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1.25 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 650, color: vbs.muted }}>
          {visibleProducts.length}
        </Typography>
      </Box>

      {isLoading ? (
        <Typography sx={{ color: vbs.muted, py: 4, textAlign: "center" }}>
          {t("common.loadingCatalog", "Loading catalog…")}
        </Typography>
      ) : visibleProducts.length ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 1.5,
            rowGap: 2,
          }}
        >
          {visibleProducts.map((product) => (
            <VbsGridTile
              key={product.documentId}
              product={product}
              priceLabel={formatMoney(resolvePrice(product), currency, t("catalog.priceQuote", "Request quote"))}
              onOpen={() => navigate(`/m/catalog/${product.documentId}`)}
            />
          ))}
        </Box>
      ) : (
        <Box sx={{ py: 4, textAlign: "center" }}>
          <Typography sx={{ fontSize: 17, fontWeight: 700 }}>
            {t("catalog.emptyTitle", "No products found")}
          </Typography>
          <Typography sx={{ color: vbs.muted, mt: 0.5, fontSize: 14.5 }}>
            {t("catalog.emptyText", "Try adjusting your search or filters.")}
          </Typography>
        </Box>
      )}
    </VbsShell>
  );
}
